import { config, oidcConfig } from '@/config';

// `app_url` claim from the OIDC id token. The login portal embeds the
// tenant's TLP host (e.g. `https://app5.timelog.com/cbbtest`) so each user is
// routed to the correct legacy backend without needing it baked into config.
// `config.tlpUrl`, when set, still wins — dev points it at the local proxy.
let appUrlFromToken: string | undefined;

export const setAppUrlFromToken = (url: string | undefined): void => {
  appUrlFromToken = url;
};

// Bootstrap from oidc-client-ts storage so URLs resolve on page reload, when
// the userManager doesn't re-emit `addUserLoaded`. Skipped in non-browser
// contexts (Playwright test discovery, etc.) where `window` is unavailable.
const bootstrapAppUrlFromStorage = (): void => {
  if (typeof window === 'undefined') return;
  try {
    const key = `oidc.user:${oidcConfig.authority}:${oidcConfig.client_id}`;
    const raw = window.localStorage.getItem(key);
    if (!raw) return;
    const stored = JSON.parse(raw) as { profile?: { app_url?: unknown } };
    const appUrl = stored?.profile?.app_url;
    if (typeof appUrl === 'string' && appUrl.length > 0) {
      appUrlFromToken = appUrl;
    }
  } catch {
    // Fall through to undefined; tlpBase() returns '' which is the prior behaviour.
  }
};

bootstrapAppUrlFromStorage();

const effectiveTlpUrl = (): string | undefined =>
  config.tlpUrl || appUrlFromToken;

// Base for legacy TLP-served URLs (profile images, iframe src, authentication
// redirects, mock pageUrl prefix matching, etc.). In dev, `config.tlpUrl`
// points at the local `scripts/tlpProxy.mjs` reverse proxy (e.g.
// `http://localhost:8080`) so root-relative URLs in legacy iframe pages
// resolve through the proxy and auth cookies land on `localhost`. In prod
// it's the per-tenant `app_url` from the id token.
export const tlpBase = (): string => effectiveTlpUrl() ?? '';

// Base for the modern Phoenix REST API. Empty string keeps requests
// same-origin in dev so the Vite proxy on `/phoenix` can intercept them.
export const phoenixBase = (): string => config.phoenixUrl ?? '';

// Absolute origin of the TLP base, used for `postMessage` targetOrigin when
// talking to the legacy iframe. Falls back to the current window origin when
// TLP is unconfigured.
export const tlpOrigin = (): string => {
  const url = effectiveTlpUrl();
  if (!url) return window.location.origin;
  try {
    return new URL(url).origin;
  } catch {
    return window.location.origin;
  }
};
